import React, { useCallback, useState } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import {
  getPublishedFundRates,
  savePublishedFundRatesBatch,
} from '../../api/publishedFundRates';
import type { PublishedFundRate } from '../../types/PublishedFundRate';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// "שערי קרנות מפורסמים" — the fund NAVs the TASE exchange publishes for every
// fund in the industry, per date (market data). IDENTITY PK recId (mirrors
// CurrencyNonSettlementDatesPage / SpecialRatesGrid). assetId + rateDate are
// part of the identity of a rate — editable only while the row is new
// (mirrors RatesPage's assetId-editable-on-add pattern). assetName is a
// read-only server-side projection (mirrors Inventory's assetName column);
// capturedAt is a server-stamped read-only DATETIME (mirrors AuditLogPage's
// loggedAt formatter). The operator may edit / the fund manager only views —
// that split is enforced server-side by permissions, so this is a normal
// editable LopCrudPage.
//
// Date-range filter (default: 2 weeks back -> today) reads via
// PublishedFundRates_viewHis — WITHOUT this the screen used to load the whole
// table unfiltered, so once the WH archiver starts moving rows older than 13
// months out, the grid would silently shrink with no user-visible signal.
// Same draft/active dual-state recipe as RatesHistoryPage; the post-save
// reload echoes the active range (fromDate/toDate on the batch body).

function fmtDateTime(value: unknown): string {
  if (!value) return '';
  return formatDateTimeIL(value, { seconds: true });
}

const isAddedRow = (params: EditableCallbackParams<PublishedFundRate>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

const columnDefs: ColDef<PublishedFundRate>[] = [
  {
    field: 'recId',
    headerName: t('publishedFundRates.columns.recId'),
    width: 100,
    // IDENTITY PK assigned by the server — never editable.
    editable: false,
    cellDataType: 'number',
    sort: 'asc',
  },
  {
    field: 'assetId',
    headerName: t('publishedFundRates.columns.assetId'),
    width: 120,
    // Part of the identity of a rate — editable only on a new row.
    editable: isAddedRow,
    cellDataType: 'number',
  },
  {
    field: 'assetName',
    headerName: t('publishedFundRates.columns.assetName'),
    width: 200,
    editable: false,
  },
  {
    field: 'rateDate',
    headerName: t('publishedFundRates.columns.rateDate'),
    width: 140,
    editable: isAddedRow,
    ...dateColumnProps<PublishedFundRate>('rateDate'),
  },
  {
    field: 'capturedAt',
    headerName: t('publishedFundRates.columns.capturedAt'),
    width: 160,
    editable: false,
    valueFormatter: (p: ValueFormatterParams<PublishedFundRate>) =>
      fmtDateTime(p.value),
  },
  {
    field: 'nav',
    headerName: t('publishedFundRates.columns.nav'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'bidRate',
    headerName: t('publishedFundRates.columns.bidRate'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'askRate',
    headerName: t('publishedFundRates.columns.askRate'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'manualRate',
    headerName: t('publishedFundRates.columns.manualRate'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
];

const newPublishedFundRateDefaults = (): Partial<PublishedFundRate> => ({
  recId: 0,
  assetId: 0,
  rateDate: null as unknown as string,
  nav: 0,
  bidRate: null,
  askRate: null,
  manualRate: null,
});

export function PublishedFundRatesPage() {
  // DRAFT — what's currently in the date inputs (free for user to edit).
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // ACTIVE — the dates that drive the current fetch. Initial mount fetches
  // with the default range; subsequent fetches happen only when the user
  // hits "Fetch", which copies draft -> active.
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getPublishedFundRates(activeFrom, activeTo),
    [activeFrom, activeTo],
  );
  const saveBatch = useCallback(
    (
      added: PublishedFundRate[],
      modified: PublishedFundRate[],
      deletedIds: unknown[],
    ) =>
      savePublishedFundRatesBatch(
        added,
        modified,
        deletedIds as number[],
        activeFrom,
        activeTo,
      ),
    [activeFrom, activeTo],
  );

  function handleApply() {
    setActiveFrom(draftFrom);
    setActiveTo(draftTo);
    setFetched(true);
  }

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={handleApply}
      />
      <LopCrudPage<PublishedFundRate>
        awaitingFetch={!fetched}
        resourceCode="publishedFundRates"
        titleKey="publishedFundRates.pageTitle"
        idField="recId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newPublishedFundRateDefaults}
      />
    </div>
  );
}
